import { CameraOutlined } from '@ant-design/icons'
import { usePostApi } from '@stagapps/redux-utils'
import { Button, Image, Modal, Space, Spin, Upload } from 'antd'
import { useEffect, useRef, useState } from 'react'

import apiCallGeneratorByName from 'app/apiCalls/verification/requestVerifyIDCard'
import UICard from 'common/ui/display/UICard'
import useEffectSkipFirstRender from 'hooks/useEffectSkipFirstRender'
import VerificationStatus from './VerificationStatus'

const refreshIDDataOnProcessing = (data = {}, callback) => {
  const { status } = data
  if (status === 'processing') {
    callback()
  }
}

function CameraComponent({
  title,
  type,
  initialData,
  callback = () => {},
  isUpload = true
}) {
  const videoRef = useRef(null)
  const canvasRef = useRef(null)

  const [isModalVisible, setIsModalVisible] = useState(false)
  const [capturedImage, setCapturedImage] = useState(null)
  const [isOpenCamera, setIsOpenCamera] = useState(false)

  const [data, setData] = useState(initialData || {})

  const apiCall = apiCallGeneratorByName(type)

  const {
    post,
    data: responseData,
    isLoading: isSubmitting,
    error
  } = usePostApi(apiCall, {
    resourceName: 'data'
  })

  useEffectSkipFirstRender(() => {
    if (!!responseData) {
      setData(responseData)
      refreshIDDataOnProcessing(responseData, callback)
    }
  }, [JSON.stringify(responseData)])

  useEffectSkipFirstRender(() => {
    if (initialData) {
      setData(initialData)

      setTimeout(() => refreshIDDataOnProcessing(initialData, callback), 2000)
    }
  }, [initialData])

  const { image_url, status } = data || {}

  useEffect(() => {
    const openCamera = async () => {
      setIsOpenCamera(true)

      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: true
        })

        if (videoRef.current) {
          videoRef.current.srcObject = stream
        }
      } catch (error) {
        console.error('Error accessing camera:', error)
      }
      setIsOpenCamera(false)
    }

    if (isModalVisible) {
      openCamera()
    }

    return () => {
      if (videoRef.current) {
        const stream = videoRef.current.srcObject
        if (stream) {
          const tracks = stream.getTracks()
          tracks.forEach(track => track.stop())
        }
      }
    }
  }, [isModalVisible])

  useEffect(() => {
    if (capturedImage) {
      const formData = new FormData()
      formData.append('file', capturedImage)
      formData.append('file_type', type)
      post(formData)
    }
  }, [capturedImage])

  const capturePhoto = () => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current
      const canvas = canvasRef.current
      const context = canvas.getContext('2d')

      const { videoWidth, videoHeight } = video
      canvas.width = videoWidth
      canvas.height = videoHeight

      context.drawImage(video, 0, 0, videoWidth, videoHeight)

      // const capturedImageDataURL = canvas.toDataURL('image/jpeg')

      canvas.toBlob(blob => {
        if (blob) {
          // Create a File object from the blob
          const imageFile = new File([blob], 'captured-image.jpg', {
            type: 'image/jpeg'
          })

          // Store the captured image
          setCapturedImage(imageFile)

          // You can now send this imageFile in a form POST request
          // using an HTTP client library like axios
        }
      }, 'image/jpeg')

      setIsModalVisible(false)
    }
  }

  return (
    <UICard className="mb-6 md:w-96">
      <div className="flex flex-row gap-4 ">
        {image_url && !error ? (
          <Image
            className="rounded object-contain bg-gray-100"
            width={84}
            height={66}
            src={image_url}
          />
        ) : (
          <Image
            className="rounded"
            width={84}
            height={66}
            src="error"
            fallback="data:image/png;base64,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"
          />
        )}
        <Space direction="vertical">
          <span className="font-semibold">{title}</span>

          {/* status only show when review */}
          {!!initialData && status ? (
            <VerificationStatus status={status} />
          ) : null}
          <div className="flex flex-row gap-2">
            <Button
              loading={isSubmitting}
              type="primary"
              onClick={() => setIsModalVisible(true)}
            >
              Chụp ảnh
            </Button>
            {isUpload ? (
              <Upload
                showUploadList={false}
                customRequest={e => setCapturedImage(e.file)}
              >
                <Button loading={isSubmitting}>Tải lên</Button>
              </Upload>
            ) : null}
          </div>
        </Space>
      </div>

      <Modal
        title={title}
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={null}
        width={720}
      >
        {isOpenCamera ? (
          <div className="w-fit mx-auto">
            <Spin />
          </div>
        ) : null}

        <video
          ref={videoRef}
          autoPlay
          playsInline
          width="100%"
          height="auto"
        ></video>
        <div className="w-fit mx-auto border-solid border-2 rounded-full border-gray-500 p-0.5 my-4">
          <Button
            className=" bg-gray-500"
            size="large"
            shape="circle"
            icon={<CameraOutlined style={{ color: 'white' }} />}
            onClick={capturePhoto}
          />
        </div>

        <canvas ref={canvasRef} style={{ display: 'none' }}></canvas>
      </Modal>
    </UICard>
  )
}

export default CameraComponent
